import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { GRANT_KEYS, GRANT_LABELS } from "~/auth/permissions";
import { setUserGrants } from "~/services/admin/access";
import { getApiErrorCode } from "~/utils/apiError";
import { useNotify } from "~/shared/ui/feedback/SnackbarProvider";
import { COLORS } from "~/ui/tokens";
import { accessErrorMessage } from "./labels";

const BACKOFFICE_ROLES = ["reader", "operator", "admin"];
// Papel mínimo para o grant valer (espelho do GRANT_MIN_ROLE do BE-0g). Gravar com papel menor é aceito,
// mas não libera nada até a pessoa subir de papel.
const OPERATOR_ONLY = { continuous_improvement: true, funding: true };
const DESCRIPTIONS = {
  continuous_improvement: "Agrupar, mover e plano de ação.",
  campaigns: "Campanhas e exportação.",
  funding: "Capital e funding no Financeiro.",
};

/**
 * Concessões somáveis ao papel (CA-F0-07). Cada clique grava na hora (PUT access/users/:id/grants com a
 * lista completa), fora do "Salvar alterações" da ficha. Otimista: o erro desfaz o switch.
 */
export default function GrantsSection({ user, isAdminTarget, canEdit, onSaved, onForbidden }) {
  const notify = useNotify();
  const [grants, setGrants] = useState(user.grants || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const sourceKey = (user.grants || []).join(",");

  useEffect(() => {
    setGrants(user.grants || []);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id, sourceKey]);

  const hasBackofficeRole = BACKOFFICE_ROLES.includes(user.role);

  const toggle = async (key) => {
    const previous = grants;
    const next = previous.includes(key) ? previous.filter((g) => g !== key) : [...previous, key];
    setGrants(next);
    setSaving(true);
    setError(null);
    try {
      const result = await setUserGrants(user.id, next);
      setGrants(result.grants);
      notify({ severity: "success", message: "Concessão atualizada." });
      onSaved?.(result);
    } catch (e) {
      setGrants(previous);
      setError(accessErrorMessage(e, "Não foi possível atualizar a concessão."));
      if (getApiErrorCode(e) === "FORBIDDEN_ROLE") onForbidden?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ color: COLORS.muted, mb: 1 }}>
        Somam-se ao papel e valem no próximo clique da pessoa. Cada mudança é gravada na hora.
      </Typography>
      <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 1, px: 1.5, py: 0.5 }}>
        {GRANT_KEYS.map((key) => {
          const checked = isAdminTarget || grants.includes(key);
          // Sem papel de backoffice o BE recusa conceder (422), mas revogar o que sobrou continua valendo.
          const blockedByRole = !hasBackofficeRole && !checked;
          const disabled = isAdminTarget || !canEdit || saving || blockedByRole;
          const ineffective = !isAdminTarget && checked && OPERATOR_ONLY[key] && user.role === "reader";
          const descId = `grant-${user.id}-${key}-desc`;
          const warnId = `grant-${user.id}-${key}-warn`;
          return (
            <Box key={key} sx={{ py: 0.5 }}>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Switch
                    size="small"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => toggle(key)}
                    inputProps={{ "aria-describedby": ineffective ? `${descId} ${warnId}` : descId }}
                  />
                }
                label={<Typography sx={{ fontWeight: 600 }}>{GRANT_LABELS[key]}</Typography>}
              />
              <Typography id={descId} variant="caption" component="p" sx={{ color: COLORS.muted, ml: 5.5 }}>
                {DESCRIPTIONS[key]}
              </Typography>
              {ineffective && (
                <Typography id={warnId} variant="caption" component="p" sx={{ color: "warning.dark", ml: 5.5 }}>
                  Só vale com papel Operador ou Admin.
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
      {isAdminTarget && <Typography variant="caption" component="p" sx={{ color: COLORS.muted, mt: 1 }}>Admin já tem todas as concessões.</Typography>}
      {!isAdminTarget && !hasBackofficeRole && (
        <Typography variant="caption" component="p" sx={{ color: COLORS.muted, mt: 1 }}>
          Concessões somam-se a um papel de Leitura, Operador ou Admin. Defina e salve um papel de backoffice para conceder.
        </Typography>
      )}
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    </Box>
  );
}
